// TaskBar position types
export type TaskBarPosition = 'top' | 'bottom' | 'left' | 'right';

// Element insertion areas
export interface TaskBarElements {
	left?: HTMLElement;
	center?: HTMLElement;
	right?: HTMLElement;
}

// TaskBar options
export interface TaskBarOptions {
	position: TaskBarPosition;
	className?: string;
}

export class SysUiTaskBar {
	private element: HTMLDivElement;
	private leftContainer: HTMLDivElement;
	private centerContainer: HTMLDivElement;
	private rightContainer: HTMLDivElement;

	constructor(elements: TaskBarElements, options: TaskBarOptions) {
		this.element = document.createElement('div');
		this.element.className = `sys-ui_taskbar ${options.className || ''}`.trim();

		// Set positioning styles
		this.element.style.position = 'fixed';
		this.element.style.zIndex = '1000';
		this.setPositionStyles(options.position);

		// Create container elements for left, center, right areas
		this.leftContainer = document.createElement('div');
		this.leftContainer.className = 'sys-ui_taskbar-left';

		this.centerContainer = document.createElement('div');
		this.centerContainer.className = 'sys-ui_taskbar-center';

		this.rightContainer = document.createElement('div');
		this.rightContainer.className = 'sys-ui_taskbar-right';

		// Set container styles based on orientation
		this.setContainerStyles(options.position);

		// Add containers to main element
		this.element.appendChild(this.leftContainer);
		this.element.appendChild(this.centerContainer);
		this.element.appendChild(this.rightContainer);

		// Insert provided elements
		if (elements.left) this.leftContainer.appendChild(elements.left);
		if (elements.center) this.centerContainer.appendChild(elements.center);
		if (elements.right) this.rightContainer.appendChild(elements.right);
	}

	private setPositionStyles(position: TaskBarPosition): void {
		switch (position) {
			case 'top':
				this.element.style.top = '0';
				this.element.style.left = '0';
				this.element.style.right = '0';
				this.element.style.height = '40px';
				break;
			case 'bottom':
				this.element.style.bottom = '0';
				this.element.style.left = '0';
				this.element.style.right = '0';
				this.element.style.height = '40px';
				break;
			case 'left':
				this.element.style.top = '0';
				this.element.style.left = '0';
				this.element.style.bottom = '0';
				this.element.style.width = '60px';
				break;
			case 'right':
				this.element.style.top = '0';
				this.element.style.right = '0';
				this.element.style.bottom = '0';
				this.element.style.width = '60px';
				break;
		}
	}

	private setContainerStyles(position: TaskBarPosition): void {
		const isHorizontal = position === 'top' || position === 'bottom';

		if (isHorizontal) {
			// Horizontal layout
			this.element.style.display = 'flex';
			this.element.style.flexDirection = 'row';
			this.element.style.alignItems = 'center';

			this.leftContainer.style.display = 'flex';
			this.leftContainer.style.alignItems = 'center';
			this.leftContainer.style.justifyContent = 'flex-start';
			this.leftContainer.style.flex = '0 0 auto';

			this.centerContainer.style.display = 'flex';
			this.centerContainer.style.alignItems = 'center';
			this.centerContainer.style.justifyContent = 'center';
			this.centerContainer.style.flex = '1 1 auto';

			this.rightContainer.style.display = 'flex';
			this.rightContainer.style.alignItems = 'center';
			this.rightContainer.style.justifyContent = 'flex-end';
			this.rightContainer.style.flex = '0 0 auto';
		} else {
			// Vertical layout
			this.element.style.display = 'flex';
			this.element.style.flexDirection = 'column';
			this.element.style.alignItems = 'center';

			this.leftContainer.style.display = 'flex';
			this.leftContainer.style.flexDirection = 'column';
			this.leftContainer.style.alignItems = 'center';
			this.leftContainer.style.justifyContent = 'flex-start';
			this.leftContainer.style.flex = '0 0 auto';

			this.centerContainer.style.display = 'flex';
			this.centerContainer.style.flexDirection = 'column';
			this.centerContainer.style.alignItems = 'center';
			this.centerContainer.style.justifyContent = 'center';
			this.centerContainer.style.flex = '1 1 auto';

			this.rightContainer.style.display = 'flex';
			this.rightContainer.style.flexDirection = 'column';
			this.rightContainer.style.alignItems = 'center';
			this.rightContainer.style.justifyContent = 'flex-end';
			this.rightContainer.style.flex = '0 0 auto';
		}
	}

	// Get the main element to append to DOM
	getElement(): HTMLDivElement {
		return this.element;
	}

	// Update elements in specific positions
	setLeftElement(element: HTMLElement): void {
		this.leftContainer.innerHTML = '';
		this.leftContainer.appendChild(element);
	}

	setCenterElement(element: HTMLElement): void {
		this.centerContainer.innerHTML = '';
		this.centerContainer.appendChild(element);
	}

	setRightElement(element: HTMLElement): void {
		this.rightContainer.innerHTML = '';
		this.rightContainer.appendChild(element);
	}

	// Clear specific positions
	clearLeft(): void {
		this.leftContainer.innerHTML = '';
	}

	clearCenter(): void {
		this.centerContainer.innerHTML = '';
	}

	clearRight(): void {
		this.rightContainer.innerHTML = '';
	}

	// Clear all positions
	clearAll(): void {
		this.clearLeft();
		this.clearCenter();
		this.clearRight();
	}

	// Remove taskbar from DOM
	destroy(): void {
		this.element.remove();
	}
}
